import React, { useEffect } from "react";

import ChatWidget from "igor-chat-widget";

import { isRoot, isTermitel, userFeatureEnabled } from "@/abilities/helpers";
import { callDoesDisappear, dispatchCallEvent, dispatchIncomingCall } from "@/actions/acdActions";
import { getAuthorizationToken } from "@/authorization_token";
import CallHistoryEntry from "@/components/acd/CallHistoryEntry";
import OverdueDocuments from "@/components/documents/OverdueDocuments";
import FlashMessagesList from "@/components/flash";
import WrongBillingCustomer from "@/components/staff/Worktimes/Tracker/WrongBillingCustomer";
import { ReactLazyLoading } from "@/containers/ReactLoading";
import ResponsiveComponent from "@/containers/ResponsiveComponent";
import { useAppDispatch, useAppSelector } from "@/hooks";
import ScaffoldHeader from "@/scaffold/ScaffoldHeader";
import TopBar from "@/scaffold/Topbar";
import { acdSelector } from "@/store/acdReducer";
import { sessionSelector } from "@/store/sessionReducer";

import BottomBar from "./BottomBar";
import FlashNews from "./FlashNews";
import MobileHeader from "./Header/MobileHeader";
import PasswordResetModal from "./PasswordResetModal";
import ProjectNews from "./ProjectNews";
import Switcher from "./Switcher";
import TaskNotificationModal from "./TaskNotificationModal";
import ToastNotifications from "./ToastNotifications";

const IS_PROD = process.env.NODE_ENV === "production";

const Sidebar = ReactLazyLoading(() => import("./Sidebar"));

type PropsType = {
  children?: React.ReactNode;
};

const IGOR_USERS = [
  "a.erdogan",
  "ckruse",
  "m.benson",
  "n.telkes",
  "c.richter",
  "d.guerman",
  "d.krysik-oleksiuk",
  "c.m.schoeneboom",
  "k.boukacem",
];

const Scaffold: React.FunctionComponent<PropsType> = ({ children }) => {
  const { currentUser, currentCustomer, currentProject } = useAppSelector(sessionSelector);
  const { showCallHistoryModal, inactiveCallContact } = useAppSelector(acdSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const handler = (ev: StorageEvent) => {
      if (ev.key === "su_token" && ev.newValue !== null) {
        document.location.href = "/";
      }
    };

    window.addEventListener("storage", handler);

    return () => {
      window.removeEventListener("storage", handler);
    };
  });

  useEffect(() => {
    if (currentCustomer.attrs?.presentationMode) {
      document.body.classList.add("presentation-mode");

      return () => {
        document.body.classList.remove("presentation-mode");
      };
    }
  }, [currentCustomer.attrs?.presentationMode]);

  async function triggerCall() {
    if (!IS_PROD) {
      const { INIT_CALL: callEvents, INCOMING_CALL: incoming } = await import("@/example_call");
      dispatch(dispatchIncomingCall(incoming));
      callEvents.forEach((ev) => dispatch(dispatchCallEvent(ev)));
    }
  }

  async function triggerCallEnd() {
    const { END_CALL: callEvents, CALL_DISAPPEARS: disappearing } = await import("@/example_call");
    callEvents.forEach((ev) => dispatch(dispatchCallEvent(ev)));
    dispatch(callDoesDisappear(disappearing));
  }

  useEffect(() => {
    if (import.meta.env.VITE_REDACT_PERSONAL_DATA === "true") {
      document.body.classList.add("TT-feature-redaction");
    }
  }, []);

  return (
    <>
      <ResponsiveComponent desktopComponent={<TopBar />} mobileComponent={<MobileHeader />} />

      <main>
        <Sidebar />

        <PasswordResetModal />

        <div className="TT-main-container">
          <ScaffoldHeader />

          <div className="TT-nav-container">
            <ToastNotifications />
            <ProjectNews />
          </div>

          <FlashMessagesList />

          <Switcher currentProject={currentProject} />

          <TaskNotificationModal />

          {isTermitel(currentUser) && (
            <>
              <FlashNews />
              {currentCustomer.identifier !== "termitel" && <WrongBillingCustomer />}

              {currentCustomer.identifier === "termitel" && IGOR_USERS.includes(currentUser.username) && (
                <ChatWidget
                  token="chatbot_dc0890b5-3d9a-4b5a-95e7-9964234cda6e"
                  initialMessage="Hi! Ich bin Igor von Termitel 👋 Wie kann ich dir heute weiterhelfen?"
                  defaultLanguage="DE"
                  supportedLanguages={["DE"]}
                  privacyUrl="https://termitel.de/datenschutz#chatbot"
                  params={{ termitool: getAuthorizationToken() }}
                />
              )}
            </>
          )}

          {children}
        </div>
      </main>

      {currentUser.attrs.e164 && showCallHistoryModal && !!inactiveCallContact && <CallHistoryEntry />}
      {userFeatureEnabled(currentUser, "documents") && !isRoot(currentUser) && <OverdueDocuments />}

      <BottomBar />

      {!IS_PROD && (
        <p id="TT-debug-menu">
          <button onClick={triggerCall}>Call</button>
          <button onClick={triggerCallEnd}>End Call</button>
        </p>
      )}
    </>
  );
};

export default Scaffold;
